import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiChevronDown, FiChevronUp, FiEdit2, FiFileText, FiHome, FiInfo, FiPlus, FiSlash, FiTrash2, FiUser } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  ActivityPanel,
  LeadPanel,
  ProjectPanel,
  useServiceUse,
} from './ServiceRelations';
import {
  blankGroup,
  blankTask,
  blankTier,
  badgeOf,
  groupAmount,
  groupsOf,
  money,
  poolOf,
  priceRange,
  tiersOf,
  uid,
} from './serviceCatalog';

const SAVE = `
  mutation SaveServiceSetup($updateService: UpdateService!) {
    updateService(updateService: $updateService) {
      id
      service
      description
      serviceId
      status
      category
      catalog
    }
  }
`;

function statusEnum(status) {
  return String(status || '').toLowerCase() === 'inactive' ? 'INACTIVE' : 'ACTIVE';
}

function blankTerms() {
  return { serviceTerms: [], responsibilities: [], exclusions: [], notes: [] };
}

function termsOf(service) {
  const raw = service?.catalog?.terms;
  const rows = (value) => (Array.isArray(value) ? value : []);
  if (!raw || typeof raw !== 'object') return blankTerms();
  return {
    serviceTerms: rows(raw.serviceTerms).map((row) => ({
      id: row.id || uid(),
      title: row.title || '',
      body: row.body || '',
    })),
    responsibilities: rows(raw.responsibilities).map((row) => ({ id: row.id || uid(), text: row.text || '' })),
    exclusions: rows(raw.exclusions).map((row) => ({ id: row.id || uid(), text: row.text || '' })),
    notes: rows(raw.notes).map((row) => ({ id: row.id || uid(), text: row.text || '' })),
  };
}

function cleanTerms(terms) {
  const lines = (rows) => (rows || [])
    .map((row) => ({ id: row.id || uid(), text: String(row.text || '').trim() }))
    .filter((row) => row.text);
  return {
    serviceTerms: (terms?.serviceTerms || [])
      .map((row) => ({
        id: row.id || uid(),
        title: String(row.title || '').trim(),
        body: String(row.body || '').trim(),
      }))
      .filter((row) => row.title || row.body),
    responsibilities: lines(terms?.responsibilities),
    exclusions: lines(terms?.exclusions),
    notes: lines(terms?.notes),
  };
}

export default function ServiceDetail({ service, onBack, onSave, onDelete }) {
  const [tab, setTab] = useState('Overview');
  const [tiers, setTiers] = useState(() => tiersOf(service));
  const [pool, setPool] = useState(() => poolOf(service));
  const [tierId, setTierId] = useState(() => tiersOf(service)[0]?.id || '');
  const [groupId, setGroupId] = useState(() => groupsOf(tiersOf(service)[0])[0]?.id || poolOf(service)[0]?.id || '');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingTier, setAddingTier] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [scopeOn, setScopeOn] = useState('');
  const [equipment, setEquipment] = useState(() =>
    Array.isArray(service.catalog?.equipment) ? service.catalog.equipment : [],
  );
  const [gallery, setGallery] = useState(() =>
    Array.isArray(service.catalog?.gallery) ? service.catalog.gallery : [],
  );
  const [terms, setTerms] = useState(() => termsOf(service));
  const [details, setDetails] = useState({
    name: service.name || '',
    description: service.description || '',
    code: service.code || '',
    category: service.category || '',
  });

  useEffect(() => {
    const next = tiersOf(service);
    const nextPool = poolOf(service);
    setTiers(next);
    setPool(nextPool);
    setTierId(next[0]?.id || '');
    setGroupId(groupsOf(next[0])[0]?.id || nextPool[0]?.id || '');
    setEquipment(Array.isArray(service.catalog?.equipment) ? service.catalog.equipment : []);
    setGallery(Array.isArray(service.catalog?.gallery) ? service.catalog.gallery : []);
    setTerms(termsOf(service));
    setDetails({
      name: service.name || '',
      description: service.description || '',
      code: service.code || '',
      category: service.category || '',
    });
  }, [service]);

  const relatedOn = tab === 'Project' || tab === 'Lead' || tab === 'Recent Activity';
  const related = useServiceUse(service.id, relatedOn);

  const tier = tiers.find((row) => row.id === tierId) || null;
  const groups = tier ? groupsOf(tier) : pool;
  const group = (tier ? groups : pool).find((row) => row.id === groupId)
    || (tier ? groups[0] : pool[0])
    || null;
  const tasks = group?.tasks || [];

  const patchTier = (id, patch) =>
    setTiers((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const patchGroup = (id, patch) => {
    const apply = (row) => {
      if (row.id !== id) return row;
      const next = { ...row, ...patch };
      return { ...next, price: groupAmount(next) };
    };
    setPool((rows) => rows.map(apply));
    setTiers((rows) => rows.map((item) => ({
      ...item,
      groups: groupsOf(item).map(apply),
    })));
  };

  const save = async (
    nextTiers = tiers,
    nextDetails = details,
    status = service.status,
    nextEquipment = equipment,
    nextGallery = gallery,
    nextTerms = terms,
    nextPool = pool,
  ) => {
    const storedTerms = cleanTerms(nextTerms);
    setSaving(true);
    try {
      const res = await graphqlRequest(SAVE, {
        updateService: {
          id: service.id,
          service: nextDetails.name.trim(),
          description: nextDetails.description.trim() || service.description || ' ',
          serviceId: nextDetails.code.trim() || service.code,
          status: statusEnum(status),
          category: nextDetails.category.trim() || null,
          catalog: {
            tiers: nextTiers.map((item) => ({
              ...item,
              groups: groupsOf(item).map((row) => ({ ...row, price: groupAmount(row) })),
            })),
            groups: nextPool.map((row) => ({ ...row, price: groupAmount(row) })),
            equipment: nextEquipment,
            gallery: nextGallery,
            terms: storedTerms,
          },
        },
      });
      const saved = res?.updateService;
      onSave?.({
        name: saved?.service || nextDetails.name,
        description: saved?.description || nextDetails.description,
        code: saved?.serviceId || nextDetails.code,
        category: saved?.category || nextDetails.category,
        badge: badgeOf({
          category: saved?.category || nextDetails.category,
          name: saved?.service || nextDetails.name,
        }),
        catalog: saved?.catalog || {
          tiers: nextTiers,
          groups: nextPool,
          equipment: nextEquipment,
          gallery: nextGallery,
          terms: storedTerms,
        },
        status: String(saved?.status || status).toLowerCase(),
      });
      toast.success('Service saved');
      setEditing(false);
    } catch (err) {
      toast.error(err?.message || 'Could not save the service');
    } finally {
      setSaving(false);
    }
  };

  const catalogueTotal = useMemo(() => {
    const rows = (tier ? groupsOf(tier) : pool).flatMap((group) => group.tasks || []);
    return rows.reduce((sum, task) => sum + (Number(task.price) || 0) * (Number(task.quantity) || 1), 0);
  }, [tier, pool]);

  return (
    <div className="svc-detail">
      <header className="svc-banner">
        <button type="button" className="svc-back" onClick={onBack} aria-label="Back">
          <FiArrowLeft />
        </button>
        <div className="svc-banner-mark">
          {service.hasImage ? <img src={service.icon} alt="" /> : <FiHome />}
        </div>
        <div className="svc-banner-copy">
          <p>
            <span>{service.code}</span>
            <em>{service.status === 'inactive' ? 'Inactive' : 'Active'}</em>
            {badgeOf(service) ? <b>{badgeOf(service)}</b> : null}
          </p>
          <h1>{service.name}</h1>
          <small>{service.description}</small>
        </div>
        <div className="svc-banner-actions">
          <button type="button" onClick={() => setEditing(true)}><FiEdit2 /> Edit</button>
          <button type="button" className="is-danger" onClick={onDelete}><FiTrash2 /> Delete</button>
        </div>
      </header>

      <nav className="svc-tabs">
        {['Overview', 'Task', 'Terms', 'Project', 'Lead', 'Recent Activity'].map((name) => (
          <button key={name} type="button" className={tab === name ? 'is-on' : ''} onClick={() => setTab(name)}>
            {name}
          </button>
        ))}
      </nav>

      {tab === 'Overview' ? (
        <Overview
          service={{ ...service, ...details, icon: service.icon, hasImage: service.hasImage }}
          tiers={tiers}
          pool={pool}
          saving={saving}
          gallery={gallery}
          onGallery={(next) => {
            setGallery(next);
            save(tiers, details, service.status, equipment, next);
          }}
          equipment={equipment}
          onEquipment={(next) => {
            setEquipment(next);
            save(tiers, details, service.status, next);
          }}
        />
      ) : null}

      {tab === 'Task' ? (
        <TaskBoard
          service={service}
          tiers={tiers}
          tier={tier}
          groups={groups}
          group={group}
          tasks={tasks}
          scopeOn={scopeOn}
          saving={saving}
          catalogueTotal={catalogueTotal}
          onPickTier={(row) => {
            setTierId(row.id);
            setGroupId(groupsOf(row)[0]?.id || '');
            setScopeOn('');
          }}
          onAddTier={() => setAddingTier(true)}
          onPickGroup={(id) => { setGroupId(id); setScopeOn(''); }}
          onAddGroup={(name, price) => {
            const created = { ...blankGroup(name), price };
            setPool((rows) => [...rows, created]);
            if (tier) patchTier(tier.id, { groups: [...groupsOf(tier), created] });
            setGroupId(created.id);
            setScopeOn('');
          }}
          onPatchGroup={patchGroup}
          onRemoveGroup={(id) => {
            const target = id || group?.id;
            if (!target) return;
            setPool((rows) => rows.filter((row) => row.id !== target));
            setTiers((rows) => rows.map((item) => ({
              ...item,
              groups: groupsOf(item).filter((row) => row.id !== target),
            })));
            if (group?.id === target) {
              const rest = (tier ? groupsOf(tier) : pool).filter((row) => row.id !== target);
              setGroupId(rest[0]?.id || '');
            }
            setScopeOn('');
          }}
          onScope={setScopeOn}
          onAddTask={() => setAddingTask(true)}
          onEditTask={setEditingTask}
          onSave={() => save()}
        />
      ) : null}

      {tab === 'Terms' ? (
        <TermsPanel
          terms={terms}
          saved={termsOf(service)}
          saving={saving}
          onChange={setTerms}
          onSave={() => save()}
        />
      ) : null}

      {tab === 'Project' ? (
        <ProjectPanel service={service} projects={related.projects} uses={related.uses} loading={related.loading} />
      ) : null}

      {tab === 'Lead' ? (
        <LeadPanel service={service} leads={related.leads} loading={related.loading} onReload={related.reload} />
      ) : null}

      {tab === 'Recent Activity' ? (
        <ActivityPanel
          service={service}
          projects={related.projects}
          uses={related.uses}
          reviews={related.reviews}
          loading={related.loading}
        />
      ) : null}

      {related.error && relatedOn ? <p className="svc-empty">{related.error}</p> : null}

      {editing ? (
        <Modal title="Edit service" onClose={() => setEditing(false)}>
          <label>Name<input value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} /></label>
          <label>
            Service code
            <input value={details.code} readOnly aria-readonly="true" />
          </label>
          <label>Category<input value={details.category} onChange={(e) => setDetails({ ...details, category: e.target.value })} placeholder="Residential" /></label>
          <label>Description<textarea rows={4} value={details.description} onChange={(e) => setDetails({ ...details, description: e.target.value })} /></label>
          <div className="svc-modal-actions">
            <button type="button" onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="is-primary" disabled={saving} onClick={() => save(tiers, details)}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      ) : null}

      {addingTier ? (
        <TierModal
          tiers={tiers}
          pool={pool}
          onClose={() => setAddingTier(false)}
          onCreate={(created) => {
            const known = new Set(pool.map((row) => row.id));
            const extra = groupsOf(created).filter((row) => row.id && !known.has(row.id));
            if (extra.length) setPool((rows) => [...rows, ...extra]);
            const next = [...tiers, created];
            setTiers(next);
            setTierId(created.id);
            setGroupId(groupsOf(created)[0]?.id || '');
            setAddingTier(false);
          }}
        />
      ) : null}

      {(addingTask || editingTask) && group ? (
        <TaskModal
          groups={groups}
          groupId={editingTask ? group.id : group.id}
          task={editingTask}
          onClose={() => { setAddingTask(false); setEditingTask(null); }}
          onCreate={(task, targetGroupId, keepOpen) => {
            const targetId = targetGroupId || group.id;
            const place = (row) => {
              const nextTasks = (row.tasks || []).filter((item) => item.id !== task.id);
              const next = row.id === targetId ? { ...row, tasks: [...nextTasks, task] } : { ...row, tasks: nextTasks };
              return { ...next, price: groupAmount(next) };
            };
            setPool((rows) => rows.map(place));
            setTiers((rows) => rows.map((item) => ({
              ...item,
              groups: groupsOf(item).map(place),
            })));
            setGroupId(targetId);
            if (!keepOpen) {
              setAddingTask(false);
              setEditingTask(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function dollars(value) {
  const text = money(value);
  return text || '$0';
}

function shownRows(rows, blank) {
  return rows.length ? rows : [blank];
}

function TermBody({ value, onChange }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = '0px';
    node.style.height = `${node.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function TermsPanel({ terms, saved, saving, onChange, onSave }) {
  const [notesOpen, setNotesOpen] = useState(true);
  const dirty = JSON.stringify(cleanTerms(terms)) !== JSON.stringify(cleanTerms(saved));
  const patchList = (key, id, patch) => onChange({
    ...terms,
    [key]: shownRows(terms[key], { id, ...(key === 'serviceTerms' ? { title: '', body: '' } : { text: '' }) })
      .map((row) => (row.id === id ? { ...row, ...patch } : row)),
  });
  const addRow = (key) => onChange({
    ...terms,
    [key]: [
      ...shownRows(terms[key], key === 'serviceTerms'
        ? { id: uid(), title: '', body: '' }
        : { id: uid(), text: '' }),
      key === 'serviceTerms' ? { id: uid(), title: '', body: '' } : { id: uid(), text: '' },
    ],
  });
  const removeRow = (key, id) => onChange({
    ...terms,
    [key]: terms[key].filter((row) => row.id !== id),
  });

  const serviceRows = shownRows(terms.serviceTerms, { id: 'draft-term', title: '', body: '' });
  const dutyRows = shownRows(terms.responsibilities, { id: 'draft-duty', text: '' });
  const exclusionRows = shownRows(terms.exclusions, { id: 'draft-exclusion', text: '' });

  return (
    <section className="svc-terms">
      <header className="svc-terms-head">
        <div>
          <h2>Terms</h2>
          <p>Manage the default terms and client requirements for this service.</p>
        </div>
        <div className="svc-terms-save">
          {dirty ? <span><FiEdit2 /> Unsaved changes</span> : null}
          <button type="button" disabled={saving || !dirty} onClick={onSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      <div className="svc-terms-note">
        <i><FiInfo /></i>
        <div>
          <strong>Automatically included with every quotation</strong>
          <p>These terms, responsibilities, and exclusions are automatically included on every quotation for this service. They can be reviewed and adjusted for an individual quotation without changing this default.</p>
        </div>
      </div>

      <article>
        <header>
          <i><FiFileText /></i>
          <div>
            <h3>Service Terms</h3>
            <p>Your general terms for this service. These are included on every quotation alongside the client&apos;s accepted scope.</p>
          </div>
        </header>
        <div className="svc-term-table">
          {serviceRows.map((row) => {
            const filled = Boolean(String(row.title || '').trim() || String(row.body || '').trim());
            return (
              <div className={`svc-term-pair${filled ? ' is-filled' : ''}`} key={row.id}>
                <input
                  value={row.title}
                  placeholder=""
                  onChange={(e) => patchList('serviceTerms', row.id, { title: e.target.value })}
                />
                <TermBody
                  value={row.body}
                  onChange={(body) => patchList('serviceTerms', row.id, { body })}
                />
                {filled ? (
                  <button type="button" aria-label="Remove term" onClick={() => removeRow('serviceTerms', row.id)}><FiTrash2 /></button>
                ) : <span />}
              </div>
            );
          })}
        </div>
        <button type="button" className="svc-term-add" onClick={() => addRow('serviceTerms')}><FiPlus /> Add Service term</button>
      </article>

      <LineList
        icon={<FiUser />}
        title="Client Responsibilities"
        hint="What the client needs to provide or disclose before and during service."
        rows={dutyRows}
        addLabel="Add Responsibility"
        onChange={(id, text) => patchList('responsibilities', id, { text })}
        onAdd={() => addRow('responsibilities')}
        onRemove={(id) => removeRow('responsibilities', id)}
      />
      <LineList
        icon={<FiSlash />}
        title="Service Exclusions"
        hint="What is not covered under this service, shown clearly to the client on their quotation."
        rows={exclusionRows}
        addLabel="Add Exclusion"
        onChange={(id, text) => patchList('exclusions', id, { text })}
        onAdd={() => addRow('exclusions')}
        onRemove={(id) => removeRow('exclusions', id)}
      />

      <article>
        <header>
          <i><FiFileText /></i>
          <div>
            <h3>Scope-Specific Notes <em>optional</em></h3>
            <p>Optional notes tied to a specific task group, shown only when that group is included in a quotation.</p>
          </div>
          <button type="button" className="svc-term-fold" onClick={() => setNotesOpen((open) => !open)} aria-label="Toggle notes">
            {notesOpen ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </header>
        {notesOpen ? (
          <>
            {terms.notes.map((row) => (
              <div className="svc-term-line" key={row.id}>
                <b />
                <input
                  value={row.text}
                  onChange={(e) => patchList('notes', row.id, { text: e.target.value })}
                />
                <button type="button" aria-label="Remove note" onClick={() => removeRow('notes', row.id)}><FiTrash2 /></button>
              </div>
            ))}
            <button type="button" className="svc-term-add" onClick={() => addRow('notes')}><FiPlus /> Add Note</button>
          </>
        ) : null}
      </article>
    </section>
  );
}

function LineList({ icon, title, hint, rows, addLabel, onChange, onAdd, onRemove }) {
  return (
    <article>
      <header>
        <i>{icon}</i>
        <div>
          <h3>{title}</h3>
          <p>{hint}</p>
        </div>
      </header>
      {rows.map((row) => (
        <div className="svc-term-line" key={row.id}>
          <b />
          <input value={row.text} onChange={(e) => onChange(row.id, e.target.value)} />
          <button type="button" aria-label={`Remove ${title}`} onClick={() => onRemove(row.id)}><FiTrash2 /></button>
        </div>
      ))}
      <button type="button" className="svc-term-add" onClick={onAdd}><FiPlus /> {addLabel}</button>
    </article>
  );
}

function TaskBoard({
  service, tiers, tier, groups, group, tasks, scopeOn, saving, catalogueTotal,
  onPickTier, onAddTier, onPickGroup, onAddGroup, onPatchGroup, onRemoveGroup,
  onScope, onAddTask, onEditTask, onSave,
}) {
  const shown = scopeOn ? tasks.filter((task) => task.id === scopeOn) : tasks;
  const picked = tasks.filter((task) => task.includedByDefault).length;
  const tierTasks = groups.flatMap((row) => (Array.isArray(row.tasks) ? row.tasks : []));
  const groupTotal = groups.reduce((sum, row) => sum + groupAmount(row), 0);
  const filled = groups.filter((row) => (row.tasks || []).length > 0).length;

  return (
    <section className="svc-task">
      <div className="svc-task-bar">
        <strong>{service.name} Tiers with task</strong>
        <span>Add groups and the tasks inside them. A tier can include groups you already created.</span>
      </div>
      <div className="svc-tiers">
        {tiers.map((row) => (
          <button
            key={row.id}
            type="button"
            className={row.id === tier?.id ? 'is-on' : ''}
            onClick={() => onPickTier(row)}
          >
            {row.code ? <small>{row.code}</small> : null}
            {row.name || 'Untitled tier'}
            <em>{dollars(row.price)}</em>
          </button>
        ))}
        <button type="button" className="svc-tier-add" onClick={onAddTier}>
          <FiPlus /> Add new tier
        </button>
      </div>
      <div className="svc-tier-stats">
        <span>Base price <strong>{dollars(tier?.price)}</strong></span>
        <span>Task groups <strong>{groups.length}</strong></span>
        <span>Used in projects <strong>{service.totalProjects || 0}</strong></span>
      </div>
      <div className="svc-task-body">
        <aside>
          <div className="svc-groups-head">
            <div>
              <span>Task groups</span>
              <strong>{dollars(groupTotal)}</strong>
            </div>
            <small>{filled}/{groups.length} created</small>
          </div>
          {groups.map((row) => (
            <div
              key={row.id}
              className={`svc-group-row${row.id === group?.id ? ' is-on' : ''}`}
              onClick={() => onPickGroup(row.id)}
            >
              <i aria-hidden>{row.id === group?.id ? '✓' : ''}</i>
              <span>
                <input
                  className="svc-group-name"
                  value={row.name || ''}
                  aria-label="Group name"
                  placeholder="Group name"
                  onClick={(e) => e.stopPropagation()}
                  onFocus={() => onPickGroup(row.id)}
                  onChange={(e) => onPatchGroup(row.id, { name: e.target.value })}
                />
                <small>{(row.tasks || []).length} tasks</small>
              </span>
              <em>{dollars(groupAmount(row))}</em>
              <button type="button" className="svc-icon svc-icon-del" aria-label={`Delete ${row.name || 'group'}`} onClick={(e) => { e.stopPropagation(); onRemoveGroup(row.id); }}>
                <FiTrash2 />
              </button>
            </div>
          ))}
          <form
            className="svc-add-group"
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              const name = String(data.get('name') || '').trim();
              if (!name) return;
              onAddGroup(name, 0);
              e.currentTarget.reset();
            }}
          >
            <p><FiPlus /> Add Group</p>
            <input name="name" placeholder="Group name" />
            <button type="submit">Add</button>
          </form>
        </aside>
        <div className="svc-group-panel">
          <div className="svc-group-head">
            <div>
              <h2>
                <FiHome />
                {group ? (
                  <input
                    className="svc-group-title"
                    value={group.name || ''}
                    aria-label="Group name"
                    placeholder="Group name"
                    onChange={(e) => onPatchGroup(group.id, { name: e.target.value })}
                  />
                ) : 'Tasks'}
              </h2>
              <span>
                {group
                  ? `${tasks.length} tasks · ${picked}/${tasks.length || 0} selected`
                  : 'Add a group, then add tasks inside it'}
              </span>
            </div>
            {group ? (
              <button type="button" onClick={onAddTask}><FiPlus /> Add task</button>
            ) : null}
          </div>
          {group && tasks.length ? (
            <div className="svc-scopes">
              <span>Scope</span>
              {tasks.map((task) => (
                <button
                  key={task.id}
                  type="button"
                  className={scopeOn === task.id ? 'is-on' : ''}
                  onClick={() => onScope(scopeOn === task.id ? '' : task.id)}
                >
                  {task.name}
                </button>
              ))}
            </div>
          ) : null}
          {group ? (
            <div className="svc-selected-bar">
              <span>{picked} included · {dollars(groupAmount(group))}</span>
              <div>
                <button type="button" className="svc-icon-del" onClick={() => onRemoveGroup(group.id)}>Delete group</button>
              </div>
            </div>
          ) : null}
          <table>
            <thead>
              <tr>
                <th />
                <th>Task</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Inclusion</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((task) => (
                <tr key={task.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(task.includedByDefault)}
                      aria-label={`Include ${task.name}`}
                      onChange={(e) => onPatchGroup(group.id, {
                        tasks: tasks.map((row) => row.id === task.id
                          ? { ...row, includedByDefault: e.target.checked, inclusion: e.target.checked ? 'included' : 'optional' }
                          : row),
                      })}
                    />
                  </td>
                  <td className="svc-task-cell">
                    <strong>{task.name}</strong>
                    <small className="is-scope"><em>Client-facing scope</em> {task.scope || '—'}</small>
                    <small className="is-note"><em>Internal work instructions</em> {task.instructions || '—'}</small>
                  </td>
                  <td>{dollars(task.price)}</td>
                  <td>
                    <span className="svc-qty">
                      <button type="button" onClick={() => onPatchGroup(group.id, { tasks: tasks.map((row) => row.id === task.id ? { ...row, quantity: Math.max(1, (Number(row.quantity) || 1) - 1) } : row) })}>−</button>
                      {task.quantity || 1}
                      <button type="button" onClick={() => onPatchGroup(group.id, { tasks: tasks.map((row) => row.id === task.id ? { ...row, quantity: (Number(row.quantity) || 1) + 1 } : row) })}>+</button>
                    </span>
                  </td>
                  <td>
                    <select
                      value={task.includedByDefault ? 'included' : 'optional'}
                      onChange={(e) => onPatchGroup(group.id, {
                        tasks: tasks.map((row) => row.id === task.id
                          ? { ...row, includedByDefault: e.target.value === 'included', inclusion: e.target.value }
                          : row),
                      })}
                    >
                      <option value="included">Included by default</option>
                      <option value="optional">Optional add-on</option>
                    </select>
                  </td>
                  <td>
                    <button type="button" className="svc-icon" aria-label="Edit task" onClick={() => onEditTask(task)}><FiEdit2 /></button>
                    <button
                      type="button"
                      className="svc-icon svc-icon-del"
                      aria-label="Delete task"
                      onClick={() => onPatchGroup(group.id, { tasks: tasks.filter((row) => row.id !== task.id) })}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <footer className="svc-savebar">
        <span>{tier?.name || 'Groups'}</span>
        <span>Catalogue total <strong>{dollars(catalogueTotal)}</strong></span>
        <span>{tierTasks.filter((task) => task.includedByDefault).length} of {tierTasks.length} tasks</span>
        <button type="button" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : tier ? `Save ${tier.name || 'tier'}` : 'Save groups'}
        </button>
      </footer>
    </section>
  );
}

function whenLabel(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Overview({ service, tiers, pool = [], equipment = [], gallery = [], onGallery, onEquipment }) {
  const [gear, setGear] = useState('');
  const badge = badgeOf(service);
  const blurb = String(service.description || '').trim();
  const library = new Map();
  pool.forEach((group) => { if (group?.id) library.set(group.id, group); });
  tiers.forEach((tier) => groupsOf(tier).forEach((group) => {
    if (group?.id && !library.has(group.id)) library.set(group.id, group);
  }));
  const scope = [];
  library.forEach((group) => {
    if (!group.name || scope.some((row) => row.name === group.name)) return;
    const note = (group.tasks || []).map((task) => task.scope).find(Boolean);
    scope.push({ name: group.name, blurb: note || `${(group.tasks || []).length} tasks` });
  });
  const taskTotal = [...library.values()].reduce(
    (sum, group) => sum + (Array.isArray(group.tasks) ? group.tasks.length : 0),
    0,
  );
  const addGallery = (fileList) => {
    const files = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    if (!files.length) return;
    Promise.all(files.map((file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    }))).then((urls) => onGallery?.([...gallery, ...urls]));
  };

  return (
    <div className="svc-overview">
      <div className="svc-overview-stats">
        <article><strong>{service.totalProjects || 0}</strong><span>Projects</span></article>
        <article><strong>{priceRange({ catalog: { tiers } }) || '—'}</strong><span>Price range</span></article>
        <article><strong>{taskTotal}</strong><span>Tasks</span></article>
        <article><strong>{whenLabel(service.updatedAt)}</strong><span>Last updated</span></article>
        <article><strong>{service.rating && service.rating !== 'N/A' ? service.rating : '—'}</strong><span>Rating</span></article>
      </div>

      <section className="svc-details-card">
        <header>
          <div>
            <p>Service details</p>
            <h2>
              {badge ? <b>{badge}</b> : null}
              {service.name}
            </h2>
            <small>{blurb ? blurb.slice(0, 90) : 'Add a short description for this service.'}</small>
          </div>
        </header>
        <div className="svc-desc-block">
          <p>{blurb || 'Add a description for this service.'}</p>
        </div>
        <div className="svc-scope-head">
          <div>
            <h3>Cleaning scope</h3>
            <p>Each group contains its own detailed tasks in the Task tab.</p>
          </div>
          <b>{scope.length} task groups</b>
        </div>
        {scope.length ? (
          <div className="svc-scope">
            {scope.map((row, index) => (
              <span key={row.name}>
                <em>{String(index + 1).padStart(2, '0')}</em>
                <strong>{row.name}</strong>
                <small>{row.blurb}</small>
              </span>
            ))}
          </div>
        ) : (
          <p className="svc-empty">Groups you add on the Task tab show up here.</p>
        )}
        <div className="svc-gear">
          <div>
            <strong>Required equipment</strong>
            <small>Saved with this service</small>
          </div>
          {equipment.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onEquipment?.(equipment.filter((row) => row !== item))}
            >
              {item} ×
            </button>
          ))}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const name = gear.trim();
              if (!name || equipment.includes(name)) return;
              onEquipment?.([...equipment, name]);
              setGear('');
            }}
          >
            <input value={gear} onChange={(e) => setGear(e.target.value)} placeholder="+ Add equipment" aria-label="Add equipment" />
          </form>
        </div>
      </section>

      <section className="svc-gallery">
        <h3>Service gallery</h3>
        <p>Extra photos for this service. The cover from Add Service stays on the catalogue card.</p>
        <div>
          {gallery.map((src, index) => (
            <figure key={`${index}-${String(src).slice(-12)}`}>
              <img src={src} alt="" />
              <button type="button" aria-label="Remove image" onClick={() => onGallery?.(gallery.filter((_, i) => i !== index))}>×</button>
            </figure>
          ))}
          <label>
            <input type="file" accept=".png,.jpg,.jpeg" multiple hidden onChange={(e) => { addGallery(e.target.files); e.target.value = ''; }} />
            <span>+</span>
            Add images
          </label>
        </div>
      </section>
    </div>
  );
}

function Modal({ title, sub, children, onClose, side, purple }) {
  return (
    <div className={`svc-modal${side ? ' svc-modal--side' : ''}`} role="dialog" aria-modal="true">
      <div>
        <header className={purple ? 'is-purple' : ''}>
          <span><strong>{title}</strong>{sub ? <small>{sub}</small> : null}</span>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>
        {children}
      </div>
    </div>
  );
}

function TierModal({ tiers, pool = [], onClose, onCreate }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('blank');
  const [sourceId, setSourceId] = useState(tiers[0]?.id || '');
  const [picked, setPicked] = useState([]);
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const groupPicker = pool.length ? (
    <>
      <p>Include groups</p>
      <div className="svc-group-pick">
        {pool.map((group) => (
          <label key={group.id} className="svc-choice svc-group-option">
            <input
              type="checkbox"
              checked={picked.includes(group.id)}
              onChange={() => setPicked((ids) => (
                ids.includes(group.id) ? ids.filter((id) => id !== group.id) : [...ids, group.id]
              ))}
            />
            <span>{group.name || 'Group'}</span>
            <small>{(group.tasks || []).length} tasks</small>
          </label>
        ))}
      </div>
    </>
  ) : null;

  return (
    <Modal title="Add new tier" onClose={onClose}>
      <label>
        Tier name
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!codeTouched) {
              setCode(e.target.value.trim().split(/\s+/).filter(Boolean).slice(0, 3).map((word) => word.slice(0, 3).toUpperCase()).join('-'));
            }
          }}
          placeholder="e.g. Premium Clean"
        />
      </label>
      {tiers.length ? (
        <>
          <p>Start from template</p>
          <label className="svc-choice">
            <input type="radio" checked={mode === 'blank'} onChange={() => setMode('blank')} />
            <span>
              Blank tier
              <small>{pool.length ? 'Choose groups you already created' : 'Start with no task groups'}</small>
            </span>
          </label>
          <label className="svc-choice">
            <input type="radio" checked={mode === 'copy'} onChange={() => setMode('copy')} />
            <span>
              Duplicate from existing tier
              <small>Copy task groups and pricing as a starting point</small>
            </span>
          </label>
          {mode === 'copy' ? (
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name || 'Untitled'}{money(tier.price) ? ` — ${money(tier.price)}` : ''}
                </option>
              ))}
            </select>
          ) : groupPicker}
        </>
      ) : groupPicker}
      <label>
        Service code
        <input value={code} onChange={(e) => { setCodeTouched(true); setCode(e.target.value); }} placeholder="Auto-suggested from name" />
        <small>Must be unique across your branch. Used in quotations and reports.</small>
      </label>
      <div className="svc-modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="is-primary"
          onClick={() => {
            if (!name.trim()) return;
            const source = tiers.find((tier) => tier.id === sourceId);
            const chosen = pool.filter((group) => picked.includes(group.id));
            const copy = mode === 'copy' && source
              ? {
                  ...blankTier(name.trim()),
                  code: code.trim(),
                  price: source.price || 0,
                  groups: groupsOf(source).map((group) => ({
                    ...group,
                    id: uid(),
                    tasks: (group.tasks || []).map((task) => ({ ...task, id: uid() })),
                  })),
                }
              : {
                  ...blankTier(name.trim()),
                  code: code.trim(),
                  groups: chosen.map((group) => ({
                    ...group,
                    tasks: [...(group.tasks || [])],
                  })),
                };
            onCreate(copy);
          }}
        >
          Create tier
        </button>
      </div>
    </Modal>
  );
}

function TaskModal({ groups, groupId, task, onClose, onCreate }) {
  const [form, setForm] = useState(task ? { ...task, groupId } : { ...blankTask(), groupId });
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const save = (keepOpen) => {
    if (!String(form.name).trim()) return;
    const { groupId: target, ...next } = form;
    onCreate(
      { ...next, name: next.name.trim(), price: Number(next.price) || 0, quantity: Number(next.quantity) || 1 },
      target,
      keepOpen && !task,
    );
    if (keepOpen && !task) setForm({ ...blankTask(), groupId: target });
  };

  return (
    <Modal title={task ? 'Edit task' : 'Add task'} sub="Define pricing, duration and required resources" onClose={onClose} side purple>
      <label>Task name *<input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Oven, inside and out" /></label>
      <label>
        Task group
        <select value={form.groupId} onChange={(e) => set({ groupId: e.target.value })}>
          {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
        </select>
      </label>
      <label>Client-facing scope<input value={form.scope} onChange={(e) => set({ scope: e.target.value })} /></label>
      <label>Internal work instructions<textarea rows={3} value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} /></label>
      <label>Default price (AUD) *<input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set({ price: e.target.value })} /></label>
      <label>Quantity<input type="number" min="1" value={form.quantity} onChange={(e) => set({ quantity: e.target.value })} /></label>
      <label className="svc-switch">
        Included by default
        <input type="checkbox" checked={form.includedByDefault} onChange={(e) => set({ includedByDefault: e.target.checked, inclusion: e.target.checked ? 'included' : 'optional' })} />
      </label>
      <div className="svc-modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        {!task ? (
          <button type="button" onClick={() => save(true)}>Save & add another</button>
        ) : null}
        <button type="button" className="is-primary" onClick={() => save(false)}>
          Save task
        </button>
      </div>
    </Modal>
  );
}
